import * as t from 'io-ts'

import { Config, ConfigCodec, ConfigWithCallbacks } from '@root/types/config'
import { IFrameAdvisorWidget } from '@root/types/widget'
import React, { Suspense } from 'react'
import { getSuggestedSizes, getUserData } from '@root/utils/app'

import { Spinner } from '@components/Spinner'
import { fold } from 'fp-ts/lib/Either'
import { formatValidationErrors } from 'io-ts-reporters'
import { logger } from '@root/utils/logger'
import { pipe } from 'fp-ts/lib/function'
import pkgVersion from '../../version'
import { isMinimized, setMinimized } from '@root/utils/store'
import { LightConfig, LightConfigCodec } from '@root/types/lightConfig'
import { createRoot, Root } from 'react-dom/client'

const FrameAdvisor = React.lazy(() => import('./FrameAdvisor'))

if (process.env.REACT_APP_DEBUG === 'true') {
  localStorage.debug = 'FA:*'
}

window.faGlobalConfig = window.faGlobalConfig || {}

export default class FrameAdvisorWidget implements IFrameAdvisorWidget {
  config?: ConfigWithCallbacks
  private root: Root | null = null

  constructor(config: ConfigWithCallbacks) {
    this.config = config

    // set default values
    if (!this.config.defaultMood) {
      this.config.defaultMood = 'harmony'
    }
    if (!this.config.defaultProductType) {
      this.config.defaultProductType = 'sunglasses'
    }
    if (!this.config.productTypes) {
      this.config.productTypes = ['sunglasses', 'eyeglasses']
    }

    if (
      !this.config.productRequestRowsLimit ||
      this.config.productRequestRowsLimit < 1 ||
      this.config.productRequestRowsLimit > 50
    ) {
      // this.config.productRequestRowsLimit = 24
      this.config.productRequestRowsLimit = 50
    }

    if (!this.config.startMinimized) {
      this.config.startMinimized = false
    }

    if (typeof this.config.enableSizeAdvisor === 'undefined') {
      this.config.enableSizeAdvisor = true
    }

    // FASA-1145 - enable size filter
    // TODO: need to delete this params in the future once we remove unused components
    this.config.enableSizeAdvisor = true

    if (typeof this.config.enableVideoMode === 'undefined') {
      this.config.enableVideoMode = true
    }

    if (typeof this.config.closePDPonAddToBag === 'undefined') {
      this.config.closePDPonAddToBag = false
    }

    // global config
    window.faGlobalConfig = {
      isTestCMS: this.config.isTestCMS || false,
    }

    logger.debug(config, 'configuration')
  }

  async render() {
    if (!this.config) {
      throw new Error('Config is not defined')
    }

    const { selector } = this.config
    const container = document.querySelector(selector)
    this.root = container ? createRoot(container) : null

    if (!container || !this.root) {
      throw new Error(
        `You requested Frame Advisor to render inside the element with the selector ${selector}, but there is no such element in the document. Check the "selector" parameter in Frame Advisor initialization or your DOM.`,
      )
    }

    // CHECK IF MINIMIZED
    const minimized = await this.isMinimize()
    if (minimized) {
      this.maximize()
    } else {
      this.root.render(
        <Suspense fallback={<Spinner />}>
          <FrameAdvisor config={this.config} />
        </Suspense>,
      )
    }
  }

  async isMinimize() {
    if (this) {
      const minimize = await isMinimized()
      return minimize
    }
  }

  async maximize() {
    if (this) {
      await setMinimized(false)
    }
  }

  async closeApp() {
    if (this.root) {
      this.root.unmount()
    }
  }

  async getUserData() {
    return getUserData(this.config)
  }

  async getSuggestedSizes(sizes: { [key: string]: number }, lowerBound = 3, upperBound = 4) {
    if (this.config?.enableSizeAdvisor) {
      return getSuggestedSizes(sizes, lowerBound, upperBound)
    }
  }

  static version() {
    return pkgVersion
  }

  static new(config: ConfigWithCallbacks) {
    const onLeft = (errors: t.Errors) => {
      const errorsToReport = formatValidationErrors(errors)
      throw Error(
        `Frame Advisor was initialized with a wrong configuration object, ${
          errorsToReport.length
        } errors found:\n - ${errorsToReport.join('\n - ')}.`,
      )
    }

    const onRight = (config: Config) => new FrameAdvisorWidget(config as ConfigWithCallbacks)

    return pipe(ConfigCodec.decode(config), fold(onLeft, onRight))
  }
}

export class FrameAdvisorLightWidget extends FrameAdvisorWidget {
  constructor(config: LightConfig) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    super(config as any)
  }

  static new(config: LightConfig) {
    const onLeft = (errors: t.Errors) => {
      const errorsToReport = formatValidationErrors(errors)
      throw Error(
        `Frame Advisor was initialized with a wrong configuration object, ${
          errorsToReport.length
        } errors found:\n - ${errorsToReport.join('\n - ')}.`,
      )
    }

    const onRight = (config: LightConfig) => new FrameAdvisorLightWidget(config)

    return pipe(LightConfigCodec.decode(config), fold(onLeft, onRight))
  }
}
